import { Button, Typography, toast } from '@montugroup/design-system';
import { ButtonGroup, Stack } from '@mui/material';
import type { StepType } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { Optional } from 'utility-types';

type NewFeatureToastContentProps = {
  // toast title
  title: ReactNode;
  // toast message
  message: string;
  // callback function when primary button is clicked
  onStartTour: () => void;
  // callback function when secondary button is clicked
  onDismiss: () => void;
};

export const NewFeatureToastContent = ({ title, message, onStartTour, onDismiss }: NewFeatureToastContentProps) => (
  <Stack direction="row" gap={1} justifyContent="space-between" width="100%">
    <Stack direction="column" justifyContent="center">
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="subtitle2">{message}</Typography>
    </Stack>

    <Stack direction="column" sx={{ minWidth: (t) => t.spacing(24) }}>
      <ButtonGroup orientation="vertical" variant="contained" aria-label="New feature tour actions">
        <Button size="small" sx={{ borderRadius: 1 }} onClick={onStartTour}>
          Take a tour
        </Button>
        <Button size="small" variant="outlined" sx={{ borderRadius: 1 }} onClick={onDismiss}>
          Dismiss
        </Button>
      </ButtonGroup>
    </Stack>
  </Stack>
);

type useFeatureNotificationProps = {
  steps: StepType[];
} & Optional<NewFeatureToastContentProps, 'onDismiss'>;

export const useFeatureNotification = ({
  title,
  message,
  steps,
  onStartTour,
  onDismiss,
}: useFeatureNotificationProps) => {
  const { setSteps, setIsOpen } = useTour();
  const newFeatureToastRef = useRef<ReturnType<typeof toast> | null>(null);

  const toastId = 'new-feature-toast';

  useEffect(() => {
    if (!newFeatureToastRef.current && steps.length > 0) {
      setSteps && setSteps(steps);
      newFeatureToastRef.current = toast.info(
        <NewFeatureToastContent
          title={title}
          message={message}
          onStartTour={() => {
            setIsOpen(true);
            onStartTour && onStartTour();
          }}
          onDismiss={() => {
            onDismiss && onDismiss();
            toast.dismiss(toastId);
          }}
        />,
        {
          toastId: toastId,
          position: 'bottom-left',
          autoClose: false,
          closeButton: false,
        },
      );
    }
  }, [message, newFeatureToastRef, onDismiss, onStartTour, setIsOpen, setSteps, steps, title]);

  // Dismiss the toast notification when not in patient details page
  useEffect(
    () => () => {
      if (!window.location.pathname.includes('patients/')) {
        toast.dismiss(toastId);
      }
    },
    [],
  );
};
